export type TransferImportDirection = 'expense' | 'income' | 'transfer';

export type TransferImportRow = {
  rowIndex: number;
  date: string | null;
  amount: number | null;
  currencyCode: string | null;
  direction: TransferImportDirection | null;
  notes: string | null;
  duplicateOfTransactionId?: number | null;
  duplicateMatch?: 'exact' | 'fuzzy' | null;
  errors?: string[];
};

/** A card payment and the other account's transfer can post this many days apart. */
export const TRANSFER_PAIR_WINDOW_DAYS = 3;

export type TransferAccountRef = {
  id: number;
  accountNumber: string | null;
  isActive?: boolean;
};

export type TransferLedgerLeg = {
  id: number;
  date: string;
  amount: number;
  currencyCode: string;
  accountId: number | null;
  toAccountId: number | null;
  kind: 'expense' | 'income' | 'transfer';
};

export type TransferChoice = {
  fromAccountId: number;
  toAccountId: number;
  linkedTransactionId: number | null;
  confidence: 'account-number' | 'exact' | 'nearby-date' | 'manual';
  dayGap: number;
};

export type TransferMatchResult = {
  suggestions: Array<TransferChoice & { rowIndex: number }>;
  ambiguous: Array<{ rowIndex: number; choices: TransferChoice[] }>;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function shiftIsoDate(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year!, (month ?? 1) - 1, (day ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

function amountsEqual(a: number, b: number): boolean {
  return Math.abs(a).toFixed(4) === Math.abs(b).toFixed(4);
}

function daysApart(a: string, b: string): number | null {
  if (!ISO_DATE.test(a) || !ISO_DATE.test(b)) return null;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const left = Date.UTC(ay!, (am ?? 1) - 1, ad ?? 1);
  const right = Date.UTC(by!, (bm ?? 1) - 1, bd ?? 1);
  return Math.abs(Math.round((left - right) / 86_400_000));
}

function digitRun(accountNumber: string | null | undefined): string | null {
  if (!accountNumber) return null;
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length < 4) return null;
  return digits;
}

function mentionsDigits(notes: string, digits: string): number {
  const full = new RegExp(`(?<!\\d)${digits}(?!\\d)`);
  if (digits.length > 4 && full.test(notes)) return digits.length;
  const last4 = digits.slice(-4);
  const tail = new RegExp(`(?<!\\d)${last4}(?!\\d)`);
  return tail.test(notes) ? 4 : 0;
}

/** The other account named in the notes, or null when zero or several match. */
export function accountMentionedInNotes(
  notes: string | null | undefined,
  accounts: TransferAccountRef[],
  sourceAccountId: number,
): number | null {
  if (!notes) return null;
  let best: { id: number; length: number } | null = null;
  let tie = false;
  for (const account of accounts) {
    if (account.id === sourceAccountId || account.isActive === false) continue;
    const digits = digitRun(account.accountNumber);
    if (!digits) continue;
    const length = mentionsDigits(notes, digits);
    if (length === 0) continue;
    if (best && length === best.length && best.id !== account.id) tie = true;
    if (!best || length > best.length) {
      best = { id: account.id, length };
      tie = false;
    }
  }
  if (!best || tie) return null;
  return best.id;
}

function flowOf(direction: TransferImportDirection | null): 'expense' | 'income' | null {
  if (direction === 'income') return 'income';
  // A single-account statement line marked "transfer" is money leaving that account.
  if (direction === 'expense' || direction === 'transfer') return 'expense';
  return null;
}

function ends(
  sourceAccountId: number,
  flow: 'expense' | 'income',
  otherAccountId: number,
): { fromAccountId: number; toAccountId: number } {
  if (flow === 'expense') {
    return { fromAccountId: sourceAccountId, toAccountId: otherAccountId };
  }
  return { fromAccountId: otherAccountId, toAccountId: sourceAccountId };
}

function counterparty(
  leg: TransferLedgerLeg,
  sourceAccountId: number,
  flow: 'expense' | 'income',
): number | null {
  if (flow === 'expense') {
    if (leg.kind === 'income' && leg.accountId != null && leg.accountId !== sourceAccountId) {
      return leg.accountId;
    }
    if (
      leg.kind === 'transfer' &&
      leg.toAccountId != null &&
      leg.toAccountId !== sourceAccountId &&
      (leg.accountId == null || leg.accountId === sourceAccountId)
    ) {
      return leg.toAccountId;
    }
    return null;
  }
  if (leg.kind === 'expense' && leg.accountId != null && leg.accountId !== sourceAccountId) {
    return leg.accountId;
  }
  if (
    leg.kind === 'transfer' &&
    leg.accountId != null &&
    leg.accountId !== sourceAccountId &&
    (leg.toAccountId == null || leg.toAccountId === sourceAccountId)
  ) {
    return leg.accountId;
  }
  return null;
}

function sameEnds(
  a: { fromAccountId: number; toAccountId: number },
  b: { fromAccountId: number; toAccountId: number },
): boolean {
  return a.fromAccountId === b.fromAccountId && a.toAccountId === b.toAccountId;
}

function dedupeChoices(choices: TransferChoice[]): TransferChoice[] {
  const byKey = new Map<string, TransferChoice>();
  for (const choice of choices) {
    const key = `${choice.fromAccountId}:${choice.toAccountId}:${choice.linkedTransactionId ?? ''}`;
    const prev = byKey.get(key);
    if (!prev || (prev.linkedTransactionId == null && choice.linkedTransactionId != null)) {
      byKey.set(key, choice);
    }
  }
  return [...byKey.values()];
}

type Built = {
  rowIndex: number;
  mention: TransferChoice | null;
  exact: TransferChoice[];
  nearby: TransferChoice[];
};

function analyze(
  row: TransferImportRow,
  sourceAccountId: number,
  accounts: TransferAccountRef[],
  legs: TransferLedgerLeg[],
): Built {
  const empty: Built = { rowIndex: row.rowIndex, mention: null, exact: [], nearby: [] };
  // An exact re-import is the same line. A fuzzy match can be the other leg.
  if (row.duplicateOfTransactionId != null && row.duplicateMatch !== 'fuzzy') return empty;
  if (row.errors && row.errors.length > 0) return empty;
  if (!row.date || row.amount == null || !row.currencyCode || !(row.amount > 0)) return empty;
  if (!ISO_DATE.test(row.date)) return empty;
  const flow = flowOf(row.direction);
  if (!flow) return empty;

  const mentioned = accountMentionedInNotes(row.notes, accounts, sourceAccountId);
  const mention: TransferChoice | null =
    mentioned == null
      ? null
      : {
          ...ends(sourceAccountId, flow, mentioned),
          linkedTransactionId: null,
          confidence: 'account-number',
          dayGap: 0,
        };

  const exact: TransferChoice[] = [];
  const nearby: TransferChoice[] = [];
  for (const leg of legs) {
    if (leg.currencyCode.toUpperCase() !== row.currencyCode.toUpperCase()) continue;
    if (!amountsEqual(leg.amount, row.amount)) continue;
    const gap = daysApart(row.date, leg.date);
    if (gap == null || gap > TRANSFER_PAIR_WINDOW_DAYS) continue;
    const other = counterparty(leg, sourceAccountId, flow);
    if (other == null) continue;
    const choice: TransferChoice = {
      ...ends(sourceAccountId, flow, other),
      linkedTransactionId: leg.id,
      confidence: gap === 0 ? 'exact' : 'nearby-date',
      dayGap: gap,
    };
    if (gap === 0) exact.push(choice);
    else nearby.push(choice);
  }

  return { rowIndex: row.rowIndex, mention, exact, nearby };
}

function preliminary(built: Built): { auto: TransferChoice | null; choices: TransferChoice[] } {
  const { mention, exact, nearby } = built;
  if (exact.length === 1) {
    const only = exact[0]!;
    if (!mention || sameEnds(mention, only)) return { auto: only, choices: [] };
    return { auto: null, choices: dedupeChoices([only, mention]) };
  }
  if (exact.length > 1) {
    return { auto: null, choices: dedupeChoices(mention ? [mention, ...exact] : exact) };
  }
  if (mention) {
    const nearbySame = nearby.filter((choice) => sameEnds(choice, mention));
    if (nearbySame.length === 1) return { auto: nearbySame[0]!, choices: [] };
    if (nearbySame.length > 1) return { auto: null, choices: dedupeChoices(nearbySame) };
    return { auto: mention, choices: [] };
  }
  if (nearby.length === 1) return { auto: nearby[0]!, choices: [] };
  if (nearby.length > 1) return { auto: null, choices: dedupeChoices(nearby) };
  return { auto: null, choices: [] };
}

/**
 * Pair import rows for `sourceAccountId` with the other account.
 * A unique opposite leg within three days is linked, including a row that was
 * only a fuzzy duplicate of that leg. A last-4 / account number in the notes
 * names the counterparty when no leg exists yet. Several fits, or two rows
 * claiming one leg, stay ambiguous. An exact duplicate is left alone.
 */
export function matchImportTransfers(input: {
  sourceAccountId: number;
  rows: TransferImportRow[];
  accounts: TransferAccountRef[];
  legs: TransferLedgerLeg[];
}): TransferMatchResult {
  const prelim = input.rows.map((row) => {
    const built = analyze(row, input.sourceAccountId, input.accounts, input.legs);
    return { rowIndex: row.rowIndex, ...preliminary(built) };
  });

  const claimers = new Map<number, number[]>();
  for (const item of prelim) {
    const id = item.auto?.linkedTransactionId;
    if (id == null) continue;
    const list = claimers.get(id) ?? [];
    list.push(item.rowIndex);
    claimers.set(id, list);
  }

  const suggestions: TransferMatchResult['suggestions'] = [];
  const ambiguous: TransferMatchResult['ambiguous'] = [];
  for (const item of prelim) {
    const id = item.auto?.linkedTransactionId;
    const contested = id != null && (claimers.get(id)?.length ?? 0) > 1;
    if (item.auto && !contested) {
      suggestions.push({ rowIndex: item.rowIndex, ...item.auto });
      continue;
    }
    const choices =
      contested && item.auto ? dedupeChoices([item.auto, ...item.choices]) : item.choices;
    if (choices.length > 0) ambiguous.push({ rowIndex: item.rowIndex, choices });
  }
  return { suggestions, ambiguous };
}

/** A commit may update this ledger row into the transfer described by `ends`. */
export function isPlausibleTransferLink(
  ends: {
    fromAccountId: number;
    toAccountId: number;
    date: string;
    amount: number;
    currencyCode: string;
  },
  leg: {
    date: string;
    amount: number;
    currencyCode: string;
    accountId: number | null;
    toAccountId: number | null;
  },
): boolean {
  if (ends.fromAccountId === ends.toAccountId) return false;
  if (leg.currencyCode.toUpperCase() !== ends.currencyCode.toUpperCase()) return false;
  if (!amountsEqual(leg.amount, ends.amount)) return false;
  const gap = daysApart(ends.date, leg.date);
  if (gap == null || gap > TRANSFER_PAIR_WINDOW_DAYS) return false;
  const ids = [leg.accountId, leg.toAccountId];
  return ids.includes(ends.fromAccountId) || ids.includes(ends.toAccountId);
}
